import { ResponseFeedback } from '~api';
import { type Vals } from '~lib';

/** @desc The per-user feedback list's filter: every record, one verdict, or only commented ones. */
export const FeedbackFilters = {
  All: 'all',
  Up: 'up',
  Down: 'down',
  Commented: 'commented',
} as const;
export type FeedbackFilter = Vals<typeof FeedbackFilters>;

const hasComment = (item: Pick<ResponseFeedback.AdminItem, 'comment'>) =>
  typeof item.comment === 'string' && item.comment.trim().length > 0;

const matches = (item: ResponseFeedback.AdminItem, filter: FeedbackFilter): boolean => {
  switch (filter) {
    case FeedbackFilters.Up:
      return item.verdict === ResponseFeedback.verdicts.Up;
    case FeedbackFilters.Down:
      return item.verdict === ResponseFeedback.verdicts.Down;
    case FeedbackFilters.Commented:
      return hasComment(item);
    default:
      return true;
  }
};

/** @desc The records `filter` keeps, order preserved. */
export const filterFeedback = (
  items: readonly ResponseFeedback.AdminItem[],
  filter: FeedbackFilter,
): ResponseFeedback.AdminItem[] => items.filter((item) => matches(item, filter));

/** @desc How many records each filter would keep, for the filter control's labels. */
export const feedbackFilterCounts = (
  items: readonly ResponseFeedback.AdminItem[],
): Record<FeedbackFilter, number> => ({
  all: items.length,
  up: items.filter((i) => matches(i, FeedbackFilters.Up)).length,
  down: items.filter((i) => matches(i, FeedbackFilters.Down)).length,
  commented: items.filter(hasComment).length,
});

/** @desc Parse a `?filter=` search value; anything unrecognised is the default (`all`). */
export const parseFeedbackFilter = (raw: unknown): FeedbackFilter =>
  (Object.values(FeedbackFilters) as unknown[]).includes(raw)
    ? (raw as FeedbackFilter)
    : FeedbackFilters.All;
